import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  IProperty,
  PropertiesApiResponse,
} from 'src/pages/Reporting/ReportingFormTypes';
import { getProperties } from 'src/services/client';

interface UsePropertiesReturn {
  data: IProperty[] | null;
  loading: boolean;
  error: string | null;
}

const fetchAndParsePropertiesDataFromAPI = async (
  userId: string,
  roleName: string
): Promise<IProperty[]> => {
  try {
    const propertiesResponse: any = await getProperties(userId, roleName);

    if (!propertiesResponse || !propertiesResponse.ok) {
      throw new Error('Failed to fetch properties');
    }

    const propertiesFromApi: PropertiesApiResponse =
      await propertiesResponse.json();
    return propertiesFromApi?.data || [];
  } catch (error) {
    console.error('Error in fetchPropertiesData:', error);
    throw error;
  }
};

const useProperties = (): UsePropertiesReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [data, setData] = useState<IProperty[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result: IProperty[] = await fetchAndParsePropertiesDataFromAPI(
          user.id,
          user?.roles[0]?.role?.name
        );
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.id, user?.roles]);

  return { data, loading, error };
};

export default useProperties;
